'use client'

import { useEffect } from 'react'
import { toast } from 'sonner'
import { getConfig } from '@/lib/config'
import { useI18n } from '@/lib/hooks/use-i18n'

/**
 * Hook to check for version updates and display notification.
 * Should be called once per session in the dashboard layout.
 */
export function useVersionCheck() {
  const { t } = useI18n()

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const config = await getConfig()

        // Only show notification if update is available
        if (config.hasUpdate && config.latestVersion) {
          // Check if user has dismissed this version in this session
          const dismissKey = `version_notification_dismissed_${config.latestVersion}`
          const isDismissed = sessionStorage.getItem(dismissKey)

          if (!isDismissed) {
            // Show persistent toast notification
            toast.info(t('versionCheck.toast.available.title', { version: config.latestVersion }), {
              description: t('versionCheck.toast.available.description'),
              duration: Infinity, // No auto-dismiss - user must manually dismiss
              closeButton: true, // Show close button for dismissing
              action: {
                label: t('versionCheck.toast.available.action'),
                onClick: () => {
                  window.open(
                    'http://git.crazylittlefish.cn/fishown-agent-v2/fishown-agent-dashboard',
                    '_blank',
                    'noopener,noreferrer'
                  )
                },
              },
              onDismiss: () => {
                // Store dismissal in session storage
                sessionStorage.setItem(dismissKey, 'true')
              },
            })

            console.log(
              `🔔 [Version Check] Update available: ${config.version} → ${config.latestVersion}`
            )
          } else {
            console.log(
              `🔕 [Version Check] Notification dismissed for version ${config.latestVersion}`
            )
          }
        } else if (config.latestVersion) {
          console.log(
            `✅ [Version Check] Running latest version: ${config.version}`
          )
        } else {
          console.log(
            `⚠️ [Version Check] Could not check for updates (offline or GitHub unavailable)`
          )
        }
      } catch (error) {
        console.error('❌ [Version Check] Failed to check version:', error)
        // Silently fail - don't disrupt user experience
      }
    }

    // Run version check
    checkVersion()
  }, [t]) // Run once on mount
}
